import React, { useCallback } from 'react';
import Select from 'react-select';
import Icon from 'components/Utils/NewIcon';

const DefaultReactSelect = React.forwardRef(
  (
    {
      onChange,
      style,
      loading,
      menuIsOpen,
      setMenuOpen,
      options,
      placeholder = 'Search',
      onInputChange,
      isSearchable = true,
      disabled = false,
      value,
      inputValue,
      filterOption,
      defaultMenuIsOpen = false,
      components,
      noOptionsMessage,
      autoFocus = false,
      blurInputOnSelect = true,
      openMenuOnFocus = false,
      preventScrollOnFocus = false,
    },
    ref,
  ) => {
    // When this select lives inside a horizontal scroll-snap container
    // (ResponsiveColumns on mobile), react-select focusing its input — on
    // autoFocus, menu-open or tap — scrolls the container to bring the input
    // into view, landing on an adjacent column. ResponsiveColumns' scroll
    // listener then reads that as a tab switch. Patch the input node's focus()
    // to force { preventScroll: true } so focusing the dropdown never moves the
    // container. react-select 3.2 calls inputRef.focus() with no options in
    // several places (and consumers call ref.current.focus()), so patching the
    // node itself covers them all.
    const handleRef = useCallback(
      (instance) => {
        if (typeof ref === 'function') ref(instance);
        else if (ref) ref.current = instance;

        if (!preventScrollOnFocus || !instance) return;

        // react-select's default export is the StateManager HOC, so the ref is
        // the StateManager; the inner Select (which holds inputRef) is at
        // instance.select.
        const input = instance.select?.inputRef ?? instance.inputRef;
        if (input && !input.__preventScrollPatched) {
          const nativeFocus = input.focus.bind(input);
          input.focus = (opts) => nativeFocus({ ...opts, preventScroll: true });
          input.__preventScrollPatched = true;
          // react-select's own autoFocus is suppressed below because it fires
          // at componentDidMount — before this ref callback, i.e. before the
          // patch. Honour it here, now that focus goes through the patch.
          if (autoFocus) input.focus();
        }
      },
      [ref, preventScrollOnFocus, autoFocus],
    );

    return (
      <div className={getClassName(isSearchable)} style={style}>
        {isSearchable && (
          <Icon src="magnifying-glass" width="15px" className="search-icon" loading={loading} />
        )}
        <Select
          ref={handleRef}
          id="search-field"
          defaultMenuIsOpen={defaultMenuIsOpen}
          menuIsOpen={menuIsOpen}
          onMenuOpen={setMenuOpen ? () => setMenuOpen(true) : undefined}
          onMenuClose={setMenuOpen ? () => setMenuOpen(false) : undefined}
          placeholder={placeholder}
          onChange={(item) => onChange(item)}
          onInputChange={onInputChange}
          options={options}
          filterOption={filterOption}
          isSearchable={isSearchable}
          disabled={disabled}
          value={value}
          inputValue={inputValue}
          components={components}
          noOptionsMessage={noOptionsMessage ? () => noOptionsMessage : undefined}
          autoFocus={preventScrollOnFocus ? false : autoFocus}
          blurInputOnSelect={blurInputOnSelect}
          openMenuOnFocus={openMenuOnFocus}
        />
        <hr />
      </div>
    );
  },
);

DefaultReactSelect.displayName = 'DefaultReactSelect';

function getClassName(isSearchable) {
  let className = 'react-select';
  if (isSearchable) className += ' searcheable';
  return className;
}

export default DefaultReactSelect;
